import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardCheck, Calendar, MapPin, User, ArrowLeft, CheckCircle, AlertCircle, FileText, Upload, Clock, Mail } from 'lucide-react';
import api from '../../api/apiClient';

interface Inspection {
  id: string;
  propertyId: number;
  propertyAddress: string;
  leaseId: number;
  tenantName: string;
  tenantEmail: string;
  scheduledAt: string;
  status: string;
  tenantConfirmed: boolean;
  inspectionType: string;
  notes: string;
  agentName: string;
  reportUrl?: string;
  reportNotes?: string;
}

export default function InspectionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportNotes, setReportNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (id) {
      loadInspection();
    }
  }, [id]);

  const loadInspection = async () => {
    setLoading(true);
    try {
      const response = await api.get<Inspection>(`/inspections/${id}`);
      setInspection(response);
    } catch (err) {
      console.error('Failed to load inspection:', err);
      setError('Failed to load inspection details');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadReport = async () => {
    if (!inspection) return;

    setUploading(true);
    setError('');
    try {
      await api.post(`/inspections/${id}/report`, {
        reportNotes,
        attachments: [],
      });
      setSuccess('Report uploaded successfully');
      setShowReportModal(false);
      loadInspection();
    } catch (err: any) {
      setError(err.message || 'Failed to upload report');
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'SCHEDULED': return 'bg-blue-100 text-blue-800';
      case 'CONFIRMED': return 'bg-purple-100 text-purple-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'ROUTINE': return 'Routine Inspection';
      case 'ENTRY': return 'Entry Inspection';
      case 'EXIT': return 'Exit Inspection';
      case 'MAINTENANCE': return 'Maintenance Inspection';
      default: return type || 'General Inspection';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUpcoming = (dateString: string) => {
    return new Date(dateString) > new Date();
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900">Inspection not found</h2>
          <button
            onClick={() => navigate('/agent/inspections')}
            className="mt-4 text-secondary hover:text-primary"
          >
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/agent/inspections')}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Inspection Details</h1>
              <p className="text-gray-600">{getTypeLabel(inspection.inspectionType)}</p>
            </div>
          </div>
        </div>
        {inspection.status !== 'COMPLETED' && !isUpcoming(inspection.scheduledAt) && (
          <button
            onClick={() => setShowReportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
          >
            <Upload className="w-5 h-5" />
            Upload Report
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Status Overview */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Status</h2>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(inspection.status)}`}>
              {inspection.status?.replace(/_/g, ' ')}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Scheduled Date</p>
                <p className="font-medium text-gray-900">{formatDate(inspection.scheduledAt)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              {inspection.tenantConfirmed ? (
                <>
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-sm text-gray-500">Tenant Status</p>
                    <p className="font-medium text-green-600">Confirmed</p>
                  </div>
                </>
              ) : (
                <>
                  <Clock className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="text-sm text-gray-500">Tenant Status</p>
                    <p className="font-medium text-orange-600">Awaiting Confirmation</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Property Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">Property</h2>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="font-medium text-gray-900 text-lg">{inspection.propertyAddress}</p>
          </div>
        </div>

        {/* Tenant Information */}
        {inspection.tenantName && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">Tenant</h2>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">{inspection.tenantName}</span>
              </div>
              {inspection.tenantEmail && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${inspection.tenantEmail}`} className="text-secondary hover:text-primary">
                    {inspection.tenantEmail}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {inspection.notes && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-primary" />
              <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
            </div>

            <p className="text-gray-600 whitespace-pre-wrap">{inspection.notes}</p>
          </div>
        )}

        {/* Report */}
        {inspection.status === 'COMPLETED' && inspection.reportNotes && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Inspection Report</h2>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-gray-700 whitespace-pre-wrap">{inspection.reportNotes}</p>
            </div>

            {inspection.reportUrl && (
              <a
                href={inspection.reportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-secondary hover:text-primary"
              >
                <FileText className="w-4 h-4" />
                View Full Report
              </a>
            )}
          </div>
        )}

        {/* Timeline placeholder */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Timeline</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2"></div>
              <div>
                <p className="font-medium text-gray-900">Inspection scheduled</p>
                <p className="text-sm text-gray-500">By {inspection.agentName || 'Agent'}</p>
              </div>
            </div>
            {inspection.tenantConfirmed && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-gray-900">Tenant confirmed availability</p>
                  <p className="text-sm text-gray-500">By {inspection.tenantName}</p>
                </div>
              </div>
            )}
            {inspection.status === 'COMPLETED' && (
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                <div>
                  <p className="font-medium text-gray-900">Inspection completed</p>
                  <p className="text-sm text-gray-500">Report uploaded</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Upload Inspection Report</h2>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Report Notes</label>
                <textarea
                  value={reportNotes}
                  onChange={(e) => setReportNotes(e.target.value)}
                  rows={6}
                  placeholder="Enter your inspection findings, observations, and recommendations..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
                />
              </div>

              <div className="p-4 bg-gray-50 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Note:</strong> Photo upload functionality coming soon. For now, please include detailed written observations.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadReport}
                  disabled={uploading || !reportNotes.trim()}
                  className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Complete Inspection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


